import type {
  Box3,
  BufferGeometry,
  Material,
  Mesh,
  MeshPhysicalMaterial,
  Object3D,
  Texture,
} from 'three';

type ThreeModule = typeof import('three');
type Point3 = { x: number; y: number; z: number };
type Bounds3 = { min: Point3; max: Point3 };

export type ProformEdgeSide = 'top' | 'right' | 'bottom' | 'left';
export type ProformEdgeDimensions = { widthMm: number; heightMm: number; depthMm: number };
export type ProformEdgeAppearance = {
  /** Caller owns this texture; the helper neither mutates nor disposes it. */
  texture?: Texture;
  color?: string;
};
export type ProformEdgeUvOptions = { tileLengthMm?: number; tileDepthMm?: number };
export type ProformEdgeGeometryOptions = {
  /** Native model mm: opt in only for a known outer rounded/banded profile. */
  outerRadiusMm?: number;
};

type EdgeTriangle = {
  start: number;
  baseMaterialIndex: number | null;
  side: ProformEdgeSide | null;
  points: [Point3, Point3, Point3];
};

type EdgeEntry = {
  mesh: Mesh;
  originalGeometry: BufferGeometry;
  originalMaterial: Material | Material[];
  geometry: BufferGeometry;
  originalUvs: Float32Array;
  baseMaterials: Material[];
  materials: Partial<Record<ProformEdgeSide, MeshPhysicalMaterial>>;
  materialIndices: Partial<Record<ProformEdgeSide, number>>;
  triangles: EdgeTriangle[];
};

export type ProformEdgeMaterials = {
  entries: EdgeEntry[];
  edgeMaterials: Record<ProformEdgeSide, MeshPhysicalMaterial[]>;
  stats: { meshes: number; triangles: Record<ProformEdgeSide, number> };
  bounds: Box3;
  dimensions: ProformEdgeDimensions;
  uvOptions: Required<ProformEdgeUvOptions>;
  appearances: Partial<Record<ProformEdgeSide, ProformEdgeAppearance>>;
  disposed: boolean;
};

const SIDES: ProformEdgeSide[] = ['top', 'right', 'bottom', 'left'];
const clamp01 = (value: number) => Math.min(Math.max(value, 0), 1);
const positiveOr = (value: number | undefined, fallback: number) =>
  value !== undefined && Number.isFinite(value) && value > 0 ? value : fallback;

/**
 * Conservative outer-side classifier for axis-aligned, normalized door geometry.
 * Uses triangle planes, not smoothed OBJ vertex normals. Faces inside an opening,
 * front/back faces are not painted merely because they touch an outer boundary.
 * Known rounded edge profiles may opt into a narrow outer-envelope strip. Without
 * that metadata, ambiguous contour faces retain the conservative flat-only rule.
 */
export function classifyProformEdgeTriangle(
  a: Point3,
  b: Point3,
  c: Point3,
  bounds: Bounds3,
  options: ProformEdgeGeometryOptions = {},
): ProformEdgeSide | null {
  const width = bounds.max.x - bounds.min.x;
  const height = bounds.max.y - bounds.min.y;
  const depth = bounds.max.z - bounds.min.z;
  if (![width, height, depth].every(value => Number.isFinite(value) && value > 0)) return null;
  if (![a, b, c].every(point => [point.x, point.y, point.z].every(Number.isFinite))) return null;

  const ab = { x: b.x - a.x, y: b.y - a.y, z: b.z - a.z };
  const ac = { x: c.x - a.x, y: c.y - a.y, z: c.z - a.z };
  const normal = {
    x: ab.y * ac.z - ab.z * ac.y,
    y: ab.z * ac.x - ab.x * ac.z,
    z: ab.x * ac.y - ab.y * ac.x,
  };
  const length = Math.hypot(normal.x, normal.y, normal.z);
  if (!Number.isFinite(length) || length <= Number.EPSILON) {
    return null;
  }

  const tolerance = Math.max(1e-7, Math.min(Math.max(width, height) * 0.00005, depth * 0.01));
  const liesOn = (axis: 'x' | 'y', plane: number) =>
    [a, b, c].every(point => Math.abs(point[axis] - plane) <= tolerance);
  if (Math.abs(normal.x) / length > 0.9999 && Math.abs(normal.z) / length <= 0.01) {
    if (liesOn('x', bounds.min.x)) return 'left';
    if (liesOn('x', bounds.max.x)) return 'right';
  }
  if (Math.abs(normal.y) / length > 0.9999 && Math.abs(normal.z) / length <= 0.01) {
    if (liesOn('y', bounds.max.y)) return 'top';
    if (liesOn('y', bounds.min.y)) return 'bottom';
  }

  // Rounded front/back lips and XY corners belong to the band as well. Restrict
  // ALL triangle vertices to one outer strip so this cannot flood the broad face
  // or an inner opening. Coplanar faces require the stricter corner-cap rule below.
  const radius = Math.min(
    positiveOr(options.outerRadiusMm, 0),
    depth / 4,
    Math.min(width, height) / 20,
  );
  if (!radius) return null;
  const outerStrip = radius + tolerance;
  const candidates: Array<{ side: ProformEdgeSide; distance: number }> = [];
  const qualify = (side: ProformEdgeSide, axis: 'x' | 'y', boundary: number) => {
    const distances = [a, b, c].map(point => Math.abs(point[axis] - boundary));
    if (distances.every(distance => distance <= outerStrip)) {
      candidates.push({ side, distance: distances.reduce((sum, value) => sum + value, 0) / 3 });
    }
  };
  qualify('top', 'y', bounds.max.y);
  qualify('right', 'x', bounds.max.x);
  qualify('bottom', 'y', bounds.min.y);
  qualify('left', 'x', bounds.min.x);
  if (Math.abs(normal.z) / length >= 0.999999) {
    // Only tiny caps belonging to TWO adjacent outer strips may use an edge
    // material. Long planar face strips, interior details and broad caps keep
    // their original finish even when a rounded profile is enabled.
    const isCorner =
      candidates.some(candidate => candidate.side === 'left' || candidate.side === 'right') &&
      candidates.some(candidate => candidate.side === 'top' || candidate.side === 'bottom');
    const isOuterFace = [bounds.min.z, bounds.max.z].some(plane =>
      [a, b, c].every(point => Math.abs(point.z - plane) <= tolerance),
    );
    if (!isCorner || !isOuterFace) return null;
  }
  // At an outer corner use the nearest side; ties have a stable clockwise order.
  candidates.sort((first, second) => first.distance - second.distance);
  if (candidates.length) return candidates[0].side;
  return null;
}

/** Clockwise strip orientation viewed from the door front, in physical mm. */
export function getProformEdgeUv(
  side: ProformEdgeSide,
  point: Point3,
  bounds: Bounds3,
  dimensions: ProformEdgeDimensions,
  options: ProformEdgeUvOptions = {},
) {
  const fraction = (value: number, min: number, max: number) =>
    [value, min, max].every(Number.isFinite) && max > min
      ? clamp01((value - min) / (max - min))
      : 0;
  const x = fraction(point.x, bounds.min.x, bounds.max.x);
  const y = fraction(point.y, bounds.min.y, bounds.max.y);
  const z = fraction(point.z, bounds.min.z, bounds.max.z);
  const width = positiveOr(dimensions.widthMm, 1);
  const height = positiveOr(dimensions.heightMm, 1);
  const depth = positiveOr(dimensions.depthMm, 1);
  const lengthMm =
    side === 'top'
      ? x * width
      : side === 'right'
        ? (1 - y) * height
        : side === 'bottom'
          ? (1 - x) * width
          : y * height;
  return {
    u: lengthMm / positiveOr(options.tileLengthMm, 600),
    v: (z * depth) / positiveOr(options.tileDepthMm, 600),
  };
}

function getBaseMaterialIndex(mesh: Mesh, start: number) {
  if (!Array.isArray(mesh.material)) return 0;
  const group = mesh.geometry.groups.find(
    candidate => start >= candidate.start && start + 3 <= candidate.start + candidate.count,
  );
  const index = group?.materialIndex;
  return index !== undefined && mesh.material[index] ? index : null;
}

function makeEdgeMaterial(THREE: ThreeModule, source: Material) {
  const physical = source as MeshPhysicalMaterial;
  const material = physical.isMeshPhysicalMaterial
    ? physical.clone()
    : new THREE.MeshPhysicalMaterial({ roughness: 0.5, metalness: 0, side: THREE.DoubleSide });
  if (!physical.isMeshPhysicalMaterial && 'color' in source) {
    material.color.copy((source as MeshPhysicalMaterial).color);
  }
  material.name = `${source.name || 'proform'}-edge`;
  // These are never inherited wood/front textures. The independently selected
  // edge is installed by setProformEdgeAppearance after preparation.
  material.map = null;
  return material;
}

/**
 * Call after normalizing/centering the loaded OBJ and installing surface materials,
 * before placing it under the non-uniform door dimension scale. No texture fetches.
 * The original mesh stays attached until at least one edge appearance is enabled.
 */
export function prepareProformEdgeMaterials(
  THREE: ThreeModule,
  object: Object3D,
  centeredBounds?: Box3,
  options: ProformEdgeGeometryOptions = {},
): ProformEdgeMaterials {
  object.updateMatrixWorld(true);
  const bounds = centeredBounds?.clone() ?? new THREE.Box3().setFromObject(object);
  const size = bounds.getSize(new THREE.Vector3());
  const instance: ProformEdgeMaterials = {
    entries: [],
    edgeMaterials: { top: [], right: [], bottom: [], left: [] },
    stats: { meshes: 0, triangles: { top: 0, right: 0, bottom: 0, left: 0 } },
    bounds,
    dimensions: { widthMm: size.x, heightMm: size.y, depthMm: size.z },
    uvOptions: { tileLengthMm: 600, tileDepthMm: 600 },
    appearances: {},
    disposed: false,
  };

  object.traverse(child => {
    const mesh = child as Mesh;
    if (!mesh.isMesh || !mesh.geometry.getAttribute('position')) return;
    const originalGeometry = mesh.geometry;
    const position = originalGeometry.getAttribute('position');
    const index = originalGeometry.getIndex();
    const count = index?.count ?? position.count;
    const triangles: EdgeTriangle[] = [];
    const foundSides = new Set<ProformEdgeSide>();
    const point = new THREE.Vector3();
    for (let start = 0; start + 2 < count; start += 3) {
      const points = [0, 1, 2].map(offset => {
        point
          .fromBufferAttribute(position, index ? index.getX(start + offset) : start + offset)
          .applyMatrix4(mesh.matrixWorld);
        return { x: point.x, y: point.y, z: point.z };
      }) as EdgeTriangle['points'];
      const baseMaterialIndex = getBaseMaterialIndex(mesh, start);
      const side =
        baseMaterialIndex === null
          ? null
          : classifyProformEdgeTriangle(points[0], points[1], points[2], bounds, options);
      triangles.push({ start, baseMaterialIndex, side, points });
      if (side) {
        foundSides.add(side);
        instance.stats.triangles[side] += 1;
      }
    }
    if (foundSides.size === 0) return;

    // Separate triangle corners allow side UVs/material groups without changing
    // a shared indexed front vertex or adding any new triangle/geometry surface.
    const geometry = index ? originalGeometry.toNonIndexed() : originalGeometry.clone();
    geometry.setDrawRange(originalGeometry.drawRange.start, originalGeometry.drawRange.count);
    const originalUvs = new Float32Array(geometry.getAttribute('position').count * 2);
    const sourceUv = geometry.getAttribute('uv');
    if (sourceUv) {
      for (let vertex = 0; vertex < sourceUv.count; vertex += 1) {
        originalUvs[vertex * 2] = sourceUv.getX(vertex);
        originalUvs[vertex * 2 + 1] = sourceUv.getY(vertex);
      }
    }
    geometry.setAttribute('uv', new THREE.BufferAttribute(originalUvs.slice(), 2));
    const baseMaterials = Array.isArray(mesh.material) ? [...mesh.material] : [mesh.material];
    const materials: EdgeEntry['materials'] = {};
    const materialIndices: EdgeEntry['materialIndices'] = {};
    let materialIndex = baseMaterials.length;
    SIDES.forEach(side => {
      if (!foundSides.has(side)) return;
      const triangle = triangles.find(candidate => candidate.side === side)!;
      const material = makeEdgeMaterial(THREE, baseMaterials[triangle.baseMaterialIndex ?? 0]);
      material.name = `${material.name}-${side}`;
      materials[side] = material;
      materialIndices[side] = materialIndex++;
      instance.edgeMaterials[side].push(material);
    });
    instance.entries.push({
      mesh,
      originalGeometry,
      originalMaterial: mesh.material,
      geometry,
      originalUvs,
      baseMaterials,
      materials,
      materialIndices,
      triangles,
    });
  });
  instance.stats.meshes = instance.entries.length;
  return instance;
}

function updateEntryUvs(instance: ProformEdgeMaterials, entry: EdgeEntry) {
  const uv = entry.geometry.getAttribute('uv');
  for (const triangle of entry.triangles) {
    if (!triangle.side) continue;
    triangle.points.forEach((point, offset) => {
      const vertex = triangle.start + offset;
      if (instance.appearances[triangle.side!]) {
        const next = getProformEdgeUv(
          triangle.side!,
          point,
          instance.bounds,
          instance.dimensions,
          instance.uvOptions,
        );
        uv.setXY(vertex, next.u, next.v);
      } else {
        uv.setXY(vertex, entry.originalUvs[vertex * 2], entry.originalUvs[vertex * 2 + 1]);
      }
    });
  }
  uv.needsUpdate = true;
}

export function updateProformEdgeUvs(
  instance: ProformEdgeMaterials,
  dimensions: ProformEdgeDimensions,
  options: ProformEdgeUvOptions = {},
) {
  if (instance.disposed) return;
  instance.dimensions = { ...dimensions };
  instance.uvOptions = {
    tileLengthMm: positiveOr(options.tileLengthMm, instance.uvOptions.tileLengthMm),
    tileDepthMm: positiveOr(options.tileDepthMm, instance.uvOptions.tileDepthMm),
  };
  instance.entries.forEach(entry => updateEntryUvs(instance, entry));
}

/** Update one side independently; undefined restores its current original surface. */
export function setProformEdgeAppearance(
  instance: ProformEdgeMaterials,
  side: ProformEdgeSide,
  appearance?: ProformEdgeAppearance,
) {
  if (instance.disposed) return;
  if (appearance) instance.appearances[side] = { ...appearance };
  else delete instance.appearances[side];
  for (const entry of instance.entries) {
    const material = entry.materials[side];
    if (material) {
      material.map = appearance?.texture ?? null;
      if (appearance) material.color.set(appearance.color ?? '#ffffff');
      material.needsUpdate = true;
    }
    const isActive = SIDES.some(
      candidate => entry.materials[candidate] && instance.appearances[candidate],
    );
    if (!isActive) {
      entry.mesh.geometry = entry.originalGeometry;
      entry.mesh.material = entry.originalMaterial;
      continue;
    }
    entry.geometry.clearGroups();
    let previous: BufferGeometry['groups'][number] | undefined;
    for (const triangle of entry.triangles) {
      const materialIndex =
        triangle.side && instance.appearances[triangle.side]
          ? entry.materialIndices[triangle.side]
          : triangle.baseMaterialIndex;
      if (materialIndex === null || materialIndex === undefined) {
        previous = undefined;
        continue;
      }
      if (
        previous &&
        previous.start + previous.count === triangle.start &&
        previous.materialIndex === materialIndex
      ) {
        previous.count += 3;
      } else {
        entry.geometry.addGroup(triangle.start, 3, materialIndex);
        previous = entry.geometry.groups[entry.geometry.groups.length - 1];
      }
    }
    updateEntryUvs(instance, entry);
    entry.mesh.geometry = entry.geometry;
    entry.mesh.material = [
      ...entry.baseMaterials,
      ...SIDES.flatMap(candidate =>
        entry.materials[candidate] ? [entry.materials[candidate]!] : [],
      ),
    ];
  }
}

/**
 * Call BEFORE the viewer's disposeObject(): restores original geometry/materials,
 * then releases only helper-owned clones. Original resources and passed textures
 * remain caller-owned. Idempotent; safely call when a model request is cancelled.
 */
export function disposeProformEdgeMaterials(instance: ProformEdgeMaterials) {
  if (instance.disposed) return;
  instance.entries.forEach(entry => {
    if (entry.mesh.geometry === entry.geometry) {
      entry.mesh.geometry = entry.originalGeometry;
      entry.mesh.material = entry.originalMaterial;
    }
    entry.geometry.dispose();
    Object.values(entry.materials).forEach(material => {
      material.map = null;
      material.dispose();
    });
  });
  instance.appearances = {};
  instance.disposed = true;
}
